import { Directive, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';

import { DOMService } from '@shared/services';

/**
 * fr-select and fr-autocomplete both render their list through fr-select-dropdown, which attaches
 * to the CDK overlay container rather than the host element. A click in an open list is therefore
 * outside the host in DOM terms and has to be recognised explicitly.
 */
const DROPDOWN_LIST_CONTAINERS = '.fr-select__options-container, .fr-autocomplete__options-container';

@Directive({ selector: '[clickOutside]' })
export class ClickOutsideDirective {
  @Output() public clickedOutside: EventEmitter<void> = new EventEmitter<void>();

  constructor(private readonly elementRef: ElementRef, private readonly domService: DOMService) {}

  @HostListener('document:click', ['$event'])
  public onOutsideClick(event: Event): void {
    // isClickInsideDatepicker is for handling  3-d library with own overlay
    // cdk overlay and ng2-flatpickr overlay have separate top parent component
    // isClickInsideDatepicker handle case when we have datepicker inside overlay
    // without isClickInsideDatepicker project overlay will be closed when user navigates between months or years
    if (
      !this.isClickConfirmationPopup(event) &&
      !this.isClickInsideComponentContent(event) &&
      !this.isClickInsideDatepicker(event) &&
      !this.isClickInsideLoader(event) &&
      !this.isClickInsideSelector(event)
    ) {
      this.clickedOutside.emit();
    }
  }

  private isClickInsideComponentContent(event: Event): boolean {
    return this.containsTarget(this.elementRef.nativeElement, event);
  }

  private isClickInsideDatepicker(event: Event): boolean {
    const element = this.domService.getElement('.flatpickr-calendar.open');

    return this.containsTarget(element, event);
  }

  private isClickInsideLoader(event: Event) {
    const element = this.domService.getElement('.loading-spinner__overlay');

    return this.containsTarget(element, event);
  }

  private isClickInsideSelector(event: Event) {
    // Every open list is checked, not just the first in the document: options stop the click
    // themselves, but the container's own padding and its "no results" item do not, and those
    // clicks would otherwise close the surrounding modal and discard the form.
    return Array.from(this.domService.getElements(DROPDOWN_LIST_CONTAINERS)).some((element) =>
      this.containsTarget(element, event)
    );
  }

  private isClickConfirmationPopup(event: Event) {
    const element = this.domService.getElement('.confirmation.card');

    return this.containsTarget(element, event);
  }

  // Element rather than HTMLElement: `contains` is a Node method, and querySelectorAll hands back
  // Elements, so narrowing here would only force casts on the callers.
  private containsTarget(element: Element, event: Event): boolean {
    return element && element.contains(event.target as Node);
  }
}
